import {
  mapFieldRefToSelector,
  FieldMapping,
  escapeCssSelector
} from './fieldMapper';

/** Normalize phase/milestone tokens for comparison (fieldRef vs data-* attributes). */
function normalizeRefKey(s: string): string {
  return s.toLowerCase().replace(/[\s_-]+/g, '_');
}

function phaseKeyMatches(
  attr: string | null,
  phaseVariants: string[]
): boolean {
  if (!attr) return false;
  const n = normalizeRefKey(attr);
  return phaseVariants.some(v => normalizeRefKey(v) === n);
}

function milestoneKeyMatches(
  nameKey: string | null,
  nameLegacy: string | null,
  milestoneVariants: string[]
): boolean {
  const candidates = [nameKey, nameLegacy].filter(Boolean) as string[];
  for (const c of candidates) {
    const nc = normalizeRefKey(c);
    if (milestoneVariants.some(v => normalizeRefKey(v) === nc)) return true;
  }
  return false;
}

/** Alert highlights target this moment's milestones only, not inherited parent tables. */
function isMilestoneAlertTargetElement(el: Element): boolean {
  const host = el.closest('[data-milestone-alert-scope]');
  if (!host) return true;
  return host.getAttribute('data-milestone-alert-scope') !== 'inherited';
}

/**
 * Resolves milestone cells for highlight/clear (phased vs non-phased, underscore variants).
 */
function findMilestoneElements(
  mapping: FieldMapping,
  ruleId: string
): Element[] {
  const mileVars = mapping._milestoneNameVariants;
  if (!mileVars?.length) return [];

  const isPhased = !!(mapping.phaseName && mapping._phaseNameVariants?.length);
  const phaseVars = mapping._phaseNameVariants || [];

  if (isPhased) {
    // Date alerts and release-readiness both target the milestone value cell (has date field)
    const useDateField =
      ruleId === 'date-past-due' || ruleId === 'release-readiness';
    const nodeList = document.querySelectorAll(
      useDateField
        ? '[data-milestone-field="date"]'
        : '[data-milestone-name-key]'
    );
    const out: Element[] = [];
    nodeList.forEach(el => {
      if (!isMilestoneAlertTargetElement(el)) return;
      const pk = el.getAttribute('data-milestone-phase-key');
      if (!pk || !phaseKeyMatches(pk, phaseVars)) return;
      const mk = el.getAttribute('data-milestone-name-key');
      const legacy = el.getAttribute('data-milestone-name');
      if (milestoneKeyMatches(mk, legacy, mileVars)) out.push(el);
    });
    return out;
  }

  // Non-phased: prefer data-milestone-name-key, fall back to data-milestone-name (legacy)
  for (const v of mileVars) {
    const safe = escapeCssSelector(v);
    const byKey = document.querySelectorAll(
      `[data-milestone-field="date"][data-milestone-name-key="${safe}"]`
    );
    if (byKey.length > 0) {
      return Array.from(byKey).filter(el => {
        if (!isMilestoneAlertTargetElement(el)) return false;
        const pk = el.getAttribute('data-milestone-phase-key');
        return pk == null || pk === '';
      });
    }
  }
  for (const v of mileVars) {
    const safe = escapeCssSelector(v);
    const byName = document.querySelectorAll(
      `[data-milestone-field="date"][data-milestone-name="${safe}"]`
    );
    if (byName.length > 0) {
      return Array.from(byName).filter(el => {
        if (!isMilestoneAlertTargetElement(el)) return false;
        const pk = el.getAttribute('data-milestone-phase-key');
        return pk == null || pk === '';
      });
    }
  }

  // release-readiness: cells may be targeted by name-key only (selector from mapper)
  if (ruleId === 'release-readiness') {
    for (const v of mileVars) {
      const safe = escapeCssSelector(v);
      const byKeyOnly = document.querySelectorAll(
        `[data-milestone-name-key="${safe}"]`
      );
      if (byKeyOnly.length > 0) {
        return Array.from(byKeyOnly).filter(el => {
          if (!isMilestoneAlertTargetElement(el)) return false;
          const pk = el.getAttribute('data-milestone-phase-key');
          return pk == null || pk === '';
        });
      }
    }
  }

  return [];
}

export interface HighlightOptions {
  issueId: string;
  fieldRef: string | null;
  ruleId: string;
  scrollToField?: boolean;
  /** When true, skip expandSection (e.g. re-applying after Features collapse/expand) */
  skipExpandSection?: boolean;
}

/**
 * Highlights a field based on fieldRef
 */
export const highlightField = (options: HighlightOptions): boolean => {
  const {
    fieldRef,
    ruleId,
    scrollToField = true,
    skipExpandSection = false
  } = options;

  const mapping = mapFieldRefToSelector(fieldRef, ruleId);
  if (!mapping) {
    console.warn(
      `No mapping found for fieldRef: ${fieldRef}, ruleId: ${ruleId}`
    );
    return false;
  }

  // Expand section if needed (before finding elements) - skip when re-applying to already-visible section
  if (!skipExpandSection) {
    expandSection(mapping.section, mapping);
    // When expanding (e.g. clicking Features product header), React re-renders async.
    // Defer find/scroll so the DOM is ready.
    if (scrollToField) {
      setTimeout(() => doFindHighlightAndScroll(options, mapping), 150);
      return true;
    }
  }

  return doFindHighlightAndScroll(options, mapping);
};

function doFindHighlightAndScroll(
  options: HighlightOptions,
  mapping: FieldMapping
): boolean {
  const { fieldRef, ruleId, scrollToField = true } = options;

  // For phased General Info dates, find the correct phase section first.
  // Use Element[] throughout (arrays from filtering); NodeList is converted via Array.from in the default branch.
  let elements: Element[] | null = null;

  if (
    mapping.section === 'general-info' &&
    mapping.phaseName &&
    mapping._phaseNameVariants
  ) {
    // For phased moments, find all date fields and filter by phase section
    const allDateFields = document.querySelectorAll(mapping.selector);
    const matchingElements: Element[] = [];
    // Store phase name variants in a variable to avoid TypeScript narrowing issues
    const phaseNameVariants = mapping._phaseNameVariants;

    allDateFields.forEach(dateField => {
      // Traverse up the DOM to find the phase section
      // Look for elements with data-phase-id or phase-related classes
      let current: Element | null = dateField;
      let phaseSection: Element | null = null;

      // Traverse up to find phase section
      while (current && current !== document.body) {
        // Check if current element or any ancestor has data-phase-id
        if (current.hasAttribute('data-phase-id')) {
          phaseSection = current;
          break;
        }

        // Check if current element is within a phase section (has phase-related classes)
        const parent = current.parentElement;
        if (parent) {
          if (parent.hasAttribute('data-phase-id')) {
            phaseSection = parent;
            break;
          }
          // Check for phase section classes
          if (
            parent.classList.contains('phaseSectionContent') ||
            parent.classList.contains('phaseInfoContactsSection') ||
            parent.querySelector('[data-phase-id]')
          ) {
            phaseSection = parent;
            break;
          }
        }
        current = parent;
      }

      if (phaseSection) {
        // Find the phase name element within this phase section
        // Regular phases: data-testid="slideout-phase-name-{id}" or class*="phaseName"
        // PCO/RTM phases: class*="rtmNameText" (editable dropdown, no phaseName class)
        const phaseNameElement = phaseSection.querySelector(
          '[data-testid^="slideout-phase-name-"], [class*="phaseName"], [class*="rtmNameText"]'
        );

        if (phaseNameElement) {
          // Get the actual phase name text from the element
          const phaseNameText = phaseNameElement.textContent?.trim() || '';

          // Try all phase name variants - match against the actual phase name element text
          const matches = phaseNameVariants.some(variant => {
            // Normalize for comparison (case-insensitive, handle spaces/underscores/dashes)
            const normalizedVariant = variant
              .toLowerCase()
              .replace(/[_\s-]/g, '');
            const normalizedPhaseName = phaseNameText
              .toLowerCase()
              .replace(/[_\s-]/g, '');

            // Also check if the phase name text exactly matches or contains the variant
            // Handle cases where displayName might include description (e.g., "GA - description")
            const phaseNameOnly = phaseNameText.split(' - ')[0].trim(); // Get just the phase name part
            const normalizedPhaseNameOnly = phaseNameOnly
              .toLowerCase()
              .replace(/[_\s-]/g, '');

            return (
              normalizedPhaseName === normalizedVariant ||
              normalizedPhaseNameOnly === normalizedVariant ||
              phaseNameText.toLowerCase() === variant.toLowerCase() ||
              phaseNameOnly.toLowerCase() === variant.toLowerCase() ||
              // Fallback: check if phase name starts with variant (for cases like "GA" matching "GA - description")
              phaseNameText
                .toLowerCase()
                .startsWith(variant.toLowerCase() + ' ') ||
              phaseNameOnly.toLowerCase().startsWith(variant.toLowerCase())
            );
          });

          if (matches) {
            matchingElements.push(dateField);
          }
        }
      }
    });

    if (matchingElements.length > 0) {
      elements = matchingElements;
    }
  } else if (
    mapping.section === 'milestones' &&
    mapping._milestoneNameVariants &&
    mapping._milestoneNameVariants.length > 0
  ) {
    elements = findMilestoneElements(mapping, ruleId);
  } else {
    // For overview / general-info and other selector-backed fields, use the selector directly
    elements = Array.from(document.querySelectorAll(mapping.selector));
  }

  if (!elements || elements.length === 0) {
    console.warn(
      `No elements found for selector: ${mapping.selector}, fieldRef: ${fieldRef}`
    );
    return false;
  }

  // Feature rows already carry their own declarative per-cell highlight, computed live from
  // Redux active-issue state in Features.tsx (colGa/colDemo/colPrd `alert-field-highlight` +
  // data-alert-* attrs, see featureAlertHighlightMap). Adding the same class here targets the
  // whole row element instead (matched by `[data-testid="features-row-{featureId}"]`), producing
  // a second, un-cleared box around the entire row — so for 'features' mappings only scroll,
  // never re-highlight; the cell-level highlight already reflects the issue's real active state.
  const shouldApplyHighlightClass = mapping.section !== 'features';

  // Highlight each element
  elements.forEach((el, index) => {
    const element = el as HTMLElement;

    if (shouldApplyHighlightClass) {
      // Add highlight class
      element.classList.add('alert-field-highlight');

      // Add data attributes for tracking
      element.setAttribute('data-alert-issue-id', options.issueId);
      element.setAttribute('data-alert-field-ref', fieldRef || 'null');
      element.setAttribute('data-alert-rule-id', ruleId);
    }

    // Scroll to first element
    if (scrollToField && index === 0) {
      scrollToElement(element);
    }
  });

  return true;
}

/**
 * Removes highlight from a field
 */
export const clearFieldHighlight = (
  fieldRef: string | null,
  ruleId: string
): void => {
  const mapping = mapFieldRefToSelector(fieldRef, ruleId);
  if (!mapping) return;

  const elements =
    mapping.section === 'milestones' &&
    mapping._milestoneNameVariants &&
    mapping._milestoneNameVariants.length > 0
      ? findMilestoneElements(mapping, ruleId)
      : Array.from(document.querySelectorAll(mapping.selector));
  elements.forEach(el => {
    const element = el as HTMLElement;
    element.classList.remove('alert-field-highlight');
    element.removeAttribute('data-alert-issue-id');
    element.removeAttribute('data-alert-field-ref');
    element.removeAttribute('data-alert-rule-id');
  });
};

/**
 * Expands a section in the slideout
 */
const expandSection = (
  section: 'overview' | 'general-info' | 'milestones' | 'features',
  mapping?: FieldMapping
): void => {
  // Features are expanded declaratively by FeaturesContainer.tsx (auto-expand effect keyed off
  // active alert issues) — useFieldHighlight.ts never calls highlightField()/expandSection() for
  // 'features' mappings (skipped alongside 'milestones'), so this branch is defensive only.
  if (section === 'features') {
    return;
  }
  if (section === 'overview') {
    const overviewSection = document.querySelector(
      '[data-testid="overview-section"]'
    );
    if (overviewSection) {
      const expandButton = overviewSection.querySelector(
        '[aria-expanded="false"]'
      );
      if (expandButton) {
        (expandButton as HTMLElement).click();
      }
    }
  } else if (section === 'general-info') {
    // For phased moments, expand the specific phase's General Info section
    if (mapping?.phaseName && mapping._phaseNameVariants) {
      // Find the phase section that matches the phase name
      const allPhaseSections = document.querySelectorAll('[data-phase-id]');
      // Store phase name variants in a variable to avoid TypeScript narrowing issues
      const phaseNameVariants = mapping._phaseNameVariants;

      for (const phaseSection of Array.from(allPhaseSections)) {
        const phaseSectionText = phaseSection.textContent || '';
        const phaseSectionHtml = phaseSection.innerHTML || '';

        const matches = phaseNameVariants.some(variant => {
          const normalizedVariant = variant
            .toLowerCase()
            .replace(/[_\s-]/g, '');
          const normalizedText = phaseSectionText
            .toLowerCase()
            .replace(/[_\s-]/g, '');
          const normalizedHtml = phaseSectionHtml
            .toLowerCase()
            .replace(/[_\s-]/g, '');

          return (
            normalizedText.includes(normalizedVariant) ||
            normalizedHtml.includes(normalizedVariant) ||
            phaseSectionText.toLowerCase().includes(variant.toLowerCase()) ||
            phaseSectionHtml.toLowerCase().includes(variant.toLowerCase())
          );
        });

        if (matches) {
          // Find the General Info section within this phase
          const generalInfoHeader = phaseSection.querySelector(
            '.infoSectionHeader, [class*="infoSectionHeader"]'
          );
          if (generalInfoHeader) {
            // Check if it's collapsed and click to expand
            const sectionContent = phaseSection.querySelector(
              '.sectionContent, [class*="sectionContent"]'
            );
            if (
              !sectionContent ||
              sectionContent.getBoundingClientRect().height === 0
            ) {
              (generalInfoHeader as HTMLElement).click();
            }
          }
          break;
        }
      }
    } else {
      // For non-phased moments, expand the main General Info section
      const generalInfoSection = document.querySelector(
        '[data-testid="general-info-section"]'
      );
      if (generalInfoSection) {
        const expandButton = generalInfoSection.querySelector(
          '[aria-expanded="false"]'
        );
        if (expandButton) {
          (expandButton as HTMLElement).click();
        }
      }
    }
  } else if (section === 'milestones') {
    const milestonesSection = document.querySelector(
      '[data-testid="milestones-section"]'
    );
    if (milestonesSection) {
      const expandButton = milestonesSection.querySelector(
        '[aria-expanded="false"]'
      );
      if (expandButton) {
        (expandButton as HTMLElement).click();
      }
    }
  }
};

/**
 * Scrolls to an element smoothly
 */
const scrollToElement = (element: HTMLElement): void => {
  const scrollContainer = document.querySelector(
    '[data-testid="slideout-content-container"]'
  ) as HTMLElement;

  if (!scrollContainer) return;

  const rect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const relativeTop = rect.top - containerRect.top;

  // Only scroll if element is not visible
  if (relativeTop < 0 || relativeTop > containerRect.height - 200) {
    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + relativeTop - 100,
      behavior: 'smooth'
    });
  }
};
